import React, { FunctionComponent, useCallback, useEffect, useRef } from 'react'
import { ImageBackground, ImageSourcePropType } from 'react-native'
import { connect, ConnectedProps, useSelector } from 'react-redux'
import { useSearchParams } from 'react-router-dom'

import styled, { useTheme } from 'styled-components/native'

import {
  COMMON_MIXPANEL_PAGE,
  COVERAGE_MESSAGING_ENTRY_POINT,
  COVERAGE_MESSAGING_EVENTS,
  DirectLinkIntent,
  DirectLinkSource,
  ExtendedPathwayOption,
  FORKED_TRIAGE_EVENTS,
  FORKED_TRIAGE_PROPERTIES,
  INDIVIDUAL_COACHING_TOOLKIT,
  PATHWAYS_ENTRY_POINTS,
  S4C_EVENTS,
  S4C_PROPERTIES,
  TREATMENT_OPTIONS,
  useFlags,
  useGetCareOptionsEventHandlers,
  useInProgressSearch,
  useMakeOnsiteModalProps,
  WELLNESS_TOOLKIT_PROMOTION,
} from '@lyrahealth-inc/shared-app-logic'
import { ForkedTriagePage, ThemeType, toJS } from '@lyrahealth-inc/ui-core-crossplatform'
import { ConstellationCloudsBackgroundMobile } from '@lyrahealth-inc/ui-core-crossplatform/src/assets'
import constellationCloudsBackgroundDesktop from '@lyrahealth-inc/ui-core-crossplatform/src/assets/constellationCloudsBackgroundDesktop.png'

import { forkedTriageNavigationRoutes } from './types'
import { useCareNavigatorModal } from '../../common/hooks/useCareNavigatorModal'
import { useDefaultMixpanelEntryPoint } from '../../common/hooks/useDefaultMixpanelEntryPoint'
import { useFetchSpeculativeOnsiteProviders } from '../../common/hooks/useFetchSpeculativeOnsiteProviders'
import { useGetCostMessagingInfo } from '../../common/hooks/useGetCostMessagingInfo'
import {
  LatestMatchesEntrypoint,
  useInitLatestMatchesAndGetProps,
} from '../../common/hooks/useInitLatestMatchesAndGetProps'
import { useToolkitAvailabilityMap } from '../../common/hooks/useToolkitAvailabilityMap'
import { useTrackViewPage } from '../../common/hooks/useTrackViewPage'
import { useUpdateLDContext } from '../../common/hooks/useUpdateLDContext'
import { getDetectedCountryISO } from '../../data/appGlobals/appGlobalsSelectors'
import {
  getHideChildSearchCTA,
  getIsBlendedCareMedsAvailable,
  getIsDirectAccessMedsSupported,
  getIsDirectAccessTherapySupported,
} from '../../data/customer/customerSelectors'
import { getSessions } from '../../data/lyraTherapy/lyraTherapySelectors'
import { trackEventWithObj } from '../../data/mixpanel'
import { TrackEventWithObjAction } from '../../data/mixpanel/types'
import { useAppDispatch } from '../../data/storeConfiguration/store'
import { postGuardianIndicated } from '../../data/user/userActions'
import {
  getId,
  getIsLoggedOutUserAssumedInternationalAndOfInternationalCustomer,
  getIsUserInternational,
  getIsUserLoggedIn,
  getIsUserTeen,
  getSelfSearchAvailable,
  getUserAge,
  getUserCountryOutOfCountryNumbersList,
  getUserCountryTollFreeNumbersList,
  getUserHasIndicatedIsGuardian,
} from '../../data/user/userSelectors'
import { handleZeroViewSelect } from '../appointmentDashboard/data/appointmentDashboardActions'
import {
  fetchLatestUserFavoriteProviders,
  setDirectLinkSource,
  setLatestMatchesSearch,
} from '../onboard/data/onboardDataActions'
import { getOnboardDirectLinkSource } from '../onboard/data/onboardSelectors'
import { getLoadingOnsiteProviders, getT1OnsiteProviders } from '../onboard/data/t1-flow/t1Selectors'
import { setSelectedSearchId } from '../triage/data/triageActions'
import { getSearches } from '../triage/data/triageSelectors'

interface ForkedTriageContainerProps extends ConnectedProps<typeof connector> {
  navigationRoutes: forkedTriageNavigationRoutes
  trackEventWithObj: TrackEventWithObjAction
}

const StyledImageBackground = styled(ImageBackground)({
  flexGrow: 1,
  marginTop: '-80px',
  zIndex: '-1',
  paddingTop: '80px',
})

const SolidBackground = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  backgroundColor: theme.colors.backgroundSecondary,
  flexGrow: 1,
  marginTop: '-80px',
  zIndex: '-1',
  paddingTop: '80px',
}))

const ForkedTriageContainer: FunctionComponent<ForkedTriageContainerProps> = ({
  hideChildSearchCTA,
  navigationRoutes,
  trackEventWithObj,
  detectedCountryIso,
  setLatestMatchesSearch,
  handleZeroViewSelect,
  setSelectedSearchId,
  fetchLatestUserFavoriteProviders,
  providerSessions,
  searches,
  isLoggedIn,
  isUserAssumedInternational,
  isUserInternational,
  userAge,
  userId,
  isMedicationConsultationAvailable,
  isDirectAccessMedsSupported,
  isDirectAccessTherapySupported,
  hasIndicatedIsGuardian,
  postGuardianIndicated,
  isUserTeen,
  customerTollFreePhoneNumbers,
  outOfCountryPhoneNumbers,
}) => {
  const dispatch = useAppDispatch()
  const [searchParams] = useSearchParams()
  const onsiteProviderAddresses = useSelector(getT1OnsiteProviders)?.size

  // used to open modal of selected pathway option when navigated from microsite to logged-in forked triage
  const selectedPathwayOption = (searchParams.get('selectedPathwayOption') ?? undefined) as
    | ExtendedPathwayOption
    | undefined
  const { forkedTriageV2Enabled: showNewForkedTriage, isOnsiteEntrypointsEnabled } = useFlags()

  // Re-fetch onsite providers iff there are none already populated
  // Clearing an existing list of onsite providers results in the component visually "blinking" for the user due to React re-rendering the component
  useFetchSpeculativeOnsiteProviders({ shouldClearOnsiteProviders: false })

  const careNavModalOpenedFromMedsConsultModal = useRef(false)
  const selfSearchAvailable = useSelector(getSelfSearchAvailable)
  const showCouplesTherapy = showNewForkedTriage && isDirectAccessTherapySupported && selfSearchAvailable
  const showFamilyTherapy = showNewForkedTriage && isDirectAccessTherapySupported && selfSearchAvailable

  const loadingOnsiteProviders = useSelector(getLoadingOnsiteProviders)
  const showOnsiteTherapy = onsiteProviderAddresses > 0 || (isOnsiteEntrypointsEnabled && !loadingOnsiteProviders)

  const directLinkSource = useSelector(getOnboardDirectLinkSource) ?? DirectLinkSource.HOMEBASE_FORKED_TRIAGE

  const { breakpoints } = useTheme()
  const updateLDContext = useUpdateLDContext()
  const isInternational = isLoggedIn ? isUserInternational : isUserAssumedInternational
  const inProgressSearch = useInProgressSearch(searches, providerSessions.appointments)
  const shouldShowChildSearchOption = !hideChildSearchCTA
  const {
    coverageModalTitleText,
    coverageModalContents,
    isCostMessagingEnabled,
    costMessagingModalCoverageInfo,
    customerDisplayName,
    numSessions,
    numberOfSessionByProgram,
    displayVisitsPerIssuePerYear,
    displayVisitsPerIssuePerYearByProgram,
  } = useGetCostMessagingInfo()
  const { availabilityByToolkit, isLoading: isLoadingToolkits } = useToolkitAvailabilityMap(
    COMMON_MIXPANEL_PAGE.FORKED_TRIAGE,
  )

  const isIndividualCoachingToolkitAvailableResult = availabilityByToolkit[INDIVIDUAL_COACHING_TOOLKIT]
  const isParentToolkitAvailableResult = availabilityByToolkit[WELLNESS_TOOLKIT_PROMOTION.PARENT]
  const mixpanelEntryPoint = useDefaultMixpanelEntryPoint()
  const { careNavigatorModal, openCareNavigatorModal, isCareNavigatorModalOpen } = useCareNavigatorModal({
    getClickEntryPoint: () =>
      careNavModalOpenedFromMedsConsultModal.current
        ? PATHWAYS_ENTRY_POINTS.MEDS_CONSULT_MODAL
        : COMMON_MIXPANEL_PAGE.FORKED_TRIAGE,
    getCareNavigatorEntryPointClickEvent: () =>
      careNavModalOpenedFromMedsConsultModal.current
        ? FORKED_TRIAGE_EVENTS.CLICK_LEARN_MORE_LINK_IN_MEDS_CONSULT_MODAL
        : S4C_EVENTS.CLICK_TO_CONTACT_CN,
  })

  // todo: add back event prop to view event when latest matches entry point is finalized [LATEST_PROVIDER_PROPERTIES.PAST_SEARCH_AVAILABLE]: latestMatchesLinkProps.hasLatestMatches
  const latestMatchesLinkProps = useInitLatestMatchesAndGetProps({
    navigateToLatestProviderMatchesProps: {
      setLatestMatchesSearch,
      setSelectedSearchId,
      inProgressSearch,
      handleZeroViewSelect,
    },
    fetchLatestUserFavoriteProviders,
    entrypoint: LatestMatchesEntrypoint.FORKED_TRIAGE,
  })

  const indicateIsGuardian = () => {
    if (isLoggedIn && userId && !hasIndicatedIsGuardian) {
      postGuardianIndicated(userId)
    }
  }

  const trackPathwayOption = (eventName: FORKED_TRIAGE_EVENTS, props?: { [key: string]: string }) => {
    trackEventWithObj({
      event: eventName,
      [S4C_PROPERTIES.AGE]: userAge,
      ...mixpanelEntryPoint,
      ...props,
    })
  }

  const {
    handleSelfSearchPress,
    handleChildSearchPress,
    handleCoachingToolkitPress,
    handleParentingToolkitPress,
    handleMedsConsultPress,
    handleCouplesTherapyPress,
    handleFamilyTherapyPress,
    handleOnsiteTherapyPress,
    trackOnCareOptionModalOpen,
    trackOnCareOptionModalClose,
  } = useGetCareOptionsEventHandlers({
    directLinkSource,
    navigationRoutes,
    indicateIsGuardian,
    trackPathwayOption,
    shouldTrackDirectPathClickEvent: showNewForkedTriage && isLoggedIn,
  })

  useTrackViewPage(COMMON_MIXPANEL_PAGE.PROVIDER_CARE_PATHWAY, undefined, {
    entryPoint: mixpanelEntryPoint,
    skipAppendPageToEventName: true,
  })

  useEffect(() => {
    if (!isLoggedIn) {
      updateLDContext({ country: detectedCountryIso })
    }
  }, [detectedCountryIso, isLoggedIn, updateLDContext])

  useEffect(() => {
    if (selectedPathwayOption) {
      dispatch(setDirectLinkSource(DirectLinkSource.MICROSITE_FORKED_TRIAGE))
    }
  }, [dispatch, selectedPathwayOption])

  const handleContactCareNavigatorPress = useCallback(
    (fromMedsConsultModal = false) => {
      careNavModalOpenedFromMedsConsultModal.current = fromMedsConsultModal
      openCareNavigatorModal()
    },
    [openCareNavigatorModal],
  )

  const { setIsModalOpen: setIsOnsiteModalOpen, ...onsiteModalProps } = useMakeOnsiteModalProps({
    onContinue: customerDisplayName != null ? handleOnsiteTherapyPress : () => {},
    isInternationalUser: isInternational,
    customerDisplayName,
    customerTollFreePhoneNumbers,
    outOfCountryPhoneNumbers,
  })

  const trackCoverageModalPress = (isOpen: boolean) => {
    trackEventWithObj({
      event: isOpen ? COVERAGE_MESSAGING_EVENTS.OPEN_COVERAGE_MODAL : COVERAGE_MESSAGING_EVENTS.CLOSE_COVERAGE_MODAL,
      page: COVERAGE_MESSAGING_ENTRY_POINT.FORKED_TRIAGE,
    })
  }

  const trackAvailablePrograms = useCallback(
    (programs: string[]) => {
      trackEventWithObj({
        event: FORKED_TRIAGE_EVENTS.FORKED_TRIAGE_AVAILABLE_PROGRAMS,
        [FORKED_TRIAGE_PROPERTIES.AVAILABLE_PROGRAMS]: programs,
      })
    },
    [trackEventWithObj],
  )

  const Background: React.ComponentType<any> = showNewForkedTriage ? SolidBackground : StyledImageBackground

  const imageBackgroundProps = {
    source: breakpoints.isMobileSized
      ? (ConstellationCloudsBackgroundMobile as ImageSourcePropType)
      : (constellationCloudsBackgroundDesktop as ImageSourcePropType),
    accessibilityIgnoresInvertColors: true,
    resizeMode: 'stretch',
  }

  return (
    <Background {...(showNewForkedTriage ? {} : imageBackgroundProps)}>
      <ForkedTriagePage
        isUserLoggedIn={isLoggedIn}
        selectedPathwayOption={selectedPathwayOption}
        showNewForkedTriage={showNewForkedTriage}
        loading={isLoadingToolkits}
        showSelfSearch={selfSearchAvailable}
        showChildSearch={shouldShowChildSearchOption}
        showCoachingToolkit={isIndividualCoachingToolkitAvailableResult.isToolkitAvailable}
        showParentingToolkit={isParentToolkitAvailableResult.isToolkitAvailable}
        showMedsConsult={!isInternational && isMedicationConsultationAvailable}
        showCouplesTherapy={showCouplesTherapy}
        showFamilyTherapy={showFamilyTherapy}
        showOnsiteTherapy={showOnsiteTherapy}
        onSelfSearchPress={handleSelfSearchPress}
        onChildSearchPress={handleChildSearchPress}
        onCoachingToolkitPress={handleCoachingToolkitPress}
        onParentingToolkitPress={handleParentingToolkitPress}
        onCouplesTherapyPress={handleCouplesTherapyPress}
        onFamilyTherapyPress={handleFamilyTherapyPress}
        onOnsiteTherapyPress={() => {
          setIsOnsiteModalOpen(true)
          trackPathwayOption(FORKED_TRIAGE_EVENTS.CLICK_ONSITE, {
            [S4C_PROPERTIES.DIRECT_LINK_SOURCE]: directLinkSource,
            [S4C_PROPERTIES.DIRECT_LINK_INTENT]: DirectLinkIntent.ONSITE_THERAPY,
            [S4C_PROPERTIES.TREATMENT_OPTION]: TREATMENT_OPTIONS.INDIVIDUAL_THERAPY,
          })
        }}
        onLearnMorePress={useCallback(() => {
          handleContactCareNavigatorPress(true)
        }, [handleContactCareNavigatorPress])}
        isDirectAccessMedsSupported={isDirectAccessMedsSupported}
        onMedConsultPress={handleMedsConsultPress}
        isInternational={isInternational}
        onContactCareNavigatorPress={() => {
          handleContactCareNavigatorPress(false)
        }}
        careNavigatorFooterEntryPointModal={careNavigatorModal}
        latestMatchesLinkProps={latestMatchesLinkProps}
        isUserTeen={isUserTeen}
        coverageModalTitleText={coverageModalTitleText}
        coverageModalContents={coverageModalContents}
        costMessagingModalCoverageInfo={costMessagingModalCoverageInfo}
        isCostMessagingEnabled={isCostMessagingEnabled}
        trackCoverageModalPress={trackCoverageModalPress}
        trackAvailablePrograms={trackAvailablePrograms}
        hideCurrentOpenModal={isCareNavigatorModalOpen}
        resetSelectedPathwayOption={useCallback(() => {
          // clear the initial selectedPathwayOption so that modal won't open again on reload from a refresh or back navigation
          if (selectedPathwayOption) {
            window.history.replaceState({}, '', window.location.pathname)
          }
        }, [selectedPathwayOption])}
        onCareOptionModalOpen={trackOnCareOptionModalOpen}
        onCareOptionModalClose={trackOnCareOptionModalClose}
        customerName={customerDisplayName}
        numSessions={numSessions}
        numberOfSessionByProgram={numberOfSessionByProgram}
        displayVisitsPerIssuePerYear={displayVisitsPerIssuePerYear}
        displayVisitsPerIssuePerYearByProgram={displayVisitsPerIssuePerYearByProgram}
        onsiteModalProps={onsiteModalProps}
      />
    </Background>
  )
}

const mapStateToProps = ($$state: any) => {
  return {
    hideChildSearchCTA: getHideChildSearchCTA($$state),
    detectedCountryIso: getDetectedCountryISO($$state),
    providerSessions: getSessions($$state),
    searches: getSearches($$state),
    isLoggedIn: getIsUserLoggedIn($$state),
    isUserAssumedInternational: getIsLoggedOutUserAssumedInternationalAndOfInternationalCustomer($$state),
    isUserInternational: getIsUserInternational($$state),
    userAge: getUserAge($$state),
    userId: getId($$state),
    hasIndicatedIsGuardian: getUserHasIndicatedIsGuardian($$state),
    isUserTeen: getIsUserTeen($$state),
    isMedicationConsultationAvailable: getIsBlendedCareMedsAvailable($$state),
    isDirectAccessMedsSupported: getIsDirectAccessMedsSupported($$state),
    isDirectAccessTherapySupported: getIsDirectAccessTherapySupported($$state),
    customerTollFreePhoneNumbers: getUserCountryTollFreeNumbersList($$state),
    outOfCountryPhoneNumbers: getUserCountryOutOfCountryNumbersList($$state),
  }
}

const mapDispatchToProps = {
  trackEventWithObj,
  setLatestMatchesSearch,
  handleZeroViewSelect,
  setSelectedSearchId,
  fetchLatestUserFavoriteProviders,
  postGuardianIndicated,
}

const connector = connect(mapStateToProps, mapDispatchToProps)

export default connector(toJS(ForkedTriageContainer))
